"use client";

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  hidePageSize?: boolean;
  /** Names the control when a page shows more than one table. */
  label?: string;
}

export const Pagination = ({
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  hidePageSize = false,
  label = "Pagination"
}: PaginationProps) => {

  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
        pages.push(i);
      }
    }
    return Array.from(new Set(pages)).sort((a, b) => a - b);
  };

  // The arrows are icon-only, so without a label a screen reader announces four
  // buttons called "button" and the user cannot tell first from previous.
  // type="button" matters too: these sit inside filter forms on several pages,
  // and a bare <button> there defaults to submit.
  //
  // The disabled guards are >= / <= rather than equality: an empty listing has
  // totalPages 0 while currentPage stays 1, and an equality check left Next and
  // Last live there, asking for page 2 and page 0 of a list with no rows.
  const navButton = "p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30";

  return (
    <nav
      aria-label={label}
      className="flex flex-wrap items-center justify-center sm:justify-end gap-2 sm:gap-4 py-3 sm:py-4 px-2"
    >
      <div className="flex items-center gap-1 flex-wrap justify-center">
        <button
          type="button"
          onClick={() => onPageChange(1)}
          disabled={currentPage <= 1}
          aria-label="First page"
          className={navButton}
        >
          <ChevronsLeft size={18} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          aria-label="Previous page"
          className={navButton}
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>

        {getPageNumbers().map((page, index, array) => (
          <div key={page} className="flex items-center">
            {index > 0 && array[index - 1] !== page - 1 && (
              // Decorative: the numbers on either side already say pages were
              // skipped, and "dot dot dot" read aloud says nothing useful.
              <span aria-hidden="true" className="mx-1 text-gray-400">...</span>
            )}
            <button
              type="button"
              onClick={() => onPageChange(page)}
              aria-label={`Page ${page}`}
              aria-current={currentPage === page ? "page" : undefined}
              className={cn(
                "w-8 h-8 rounded-md text-sm font-medium transition-colors",
                currentPage === page ? "bg-[#E6F0FF] text-[#0055FF]" : "text-gray-500 hover:bg-gray-50"
              )}
            >
              {page}
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          aria-label="Next page"
          className={navButton}
        >
          <ChevronRight size={18} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage >= totalPages}
          aria-label="Last page"
          className={navButton}
        >
          <ChevronsRight size={18} aria-hidden="true" />
        </button>
      </div>

      {!hidePageSize && (
      <div className="relative group">
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          aria-label="Rows per page"
          className="appearance-none bg-white border border-[#EBEBEF] text-[#05004E] text-sm rounded-md pl-3 pr-8 py-1.5 focus:outline-none cursor-pointer"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
        <ChevronDown size={14} aria-hidden="true" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      </div>
      )}
    </nav>
  );
};
